import React, { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import axios from 'axios';
import Header from '../components/Header';
import Footer from '../components/Footer';
import SEO from '../components/SEO';
import { 
  Clock, Eye, Calendar, User, Tag, ArrowLeft, 
  Share2, Twitter, Linkedin, Facebook, Link as LinkIcon,
  BookOpen, ChevronRight
} from 'lucide-react';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const InsightDetail = () => {
  const { slug } = useParams();
  const [blog, setBlog] = useState(null);
  const [relatedBlogs, setRelatedBlogs] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchBlog();
  }, [slug]);

  const fetchBlog = async () => {
    try {
      setLoading(true);
      const response = await axios.get(`${API}/blogs/${slug}`);
      setBlog(response.data);
      
      // Fetch related blogs
      const relatedResponse = await axios.get(`${API}/blogs/${slug}/related`);
      setRelatedBlogs(relatedResponse.data || []);
    } catch (error) {
      console.error('Error fetching blog:', error);
      toast.error('Failed to load article');
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('en-IN', {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };

  // Use share endpoint URL for social media (provides proper OG tags)
  const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
  const shareUrl = `${BACKEND_URL}/api/share/insights/${slug}`;
  const directUrl = typeof window !== 'undefined' ? window.location.href : '';
  
  const handleShare = (platform) => {
    const title = blog?.title || '';
    const urls = {
      twitter: `https://twitter.com/intent/tweet?url=${encodeURIComponent(shareUrl)}&text=${encodeURIComponent(title)}`,
      linkedin: `https://www.linkedin.com/sharing/share-offsite/?url=${encodeURIComponent(shareUrl)}`,
      facebook: `https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(shareUrl)}`
    };
    
    if (platform === 'copy') {
      navigator.clipboard.writeText(directUrl);
      toast.success('Link copied to clipboard!');
      return;
    }
    
    window.open(urls[platform], '_blank', 'width=600,height=400');
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-white">
        <Header />
        <div className="max-w-4xl mx-auto px-6 md:px-12 py-12">
          <div className="animate-pulse">
            <div className="h-4 w-32 bg-gray-200 rounded mb-4"></div>
            <div className="h-10 w-3/4 bg-gray-200 rounded mb-4"></div>
            <div className="h-6 w-1/2 bg-gray-200 rounded mb-8"></div>
            <div className="h-64 w-full bg-gray-200 rounded mb-8"></div>
            <div className="space-y-4">
              <div className="h-4 w-full bg-gray-200 rounded"></div>
              <div className="h-4 w-full bg-gray-200 rounded"></div>
              <div className="h-4 w-3/4 bg-gray-200 rounded"></div>
            </div>
          </div>
        </div>
        <Footer />
      </div>
    );
  }

  if (!blog) {
    return (
      <div className="min-h-screen bg-white">
        <Header />
        <div className="max-w-4xl mx-auto px-6 md:px-12 py-24 text-center">
          <BookOpen className="w-16 h-16 mx-auto text-[#e5e5e5] mb-4" />
          <h1 className="font-playfair text-3xl font-bold mb-4 text-[#1a1a1a]">Article Not Found</h1>
          <p className="font-manrope text-[#737373] mb-8">
            The article you're looking for doesn't exist or has been removed.
          </p>
          <Link
            to="/insights"
            className="inline-flex items-center gap-2 px-6 py-3 bg-[#D4AF37] text-[#0d0d0d] rounded-lg font-manrope hover:bg-[#B8960F] transition-colors"
          >
            <ArrowLeft className="w-4 h-4" />
            Back to Insights
          </Link>
        </div>
        <Footer />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-white">
      <SEO 
        title={blog.meta_title || blog.title}
        description={blog.meta_description || blog.excerpt}
        keywords={blog.tags?.join(', ')}
        canonical={`/insights/${blog.slug}`}
        ogType="article"
        ogImage={blog.featured_image}
      />
      <Header />
      
      <article className="max-w-4xl mx-auto px-6 md:px-12 py-12">
        {/* Breadcrumb */}
        <nav className="flex items-center gap-2 text-sm text-[#737373] mb-8">
          <Link to="/" className="hover:text-[#D4AF37]">Home</Link>
          <ChevronRight className="w-4 h-4" />
          <Link to="/insights" className="hover:text-[#D4AF37]">Insights</Link>
          <ChevronRight className="w-4 h-4" />
          <span className="text-[#1a1a1a] truncate max-w-[200px]">{blog.title}</span>
        </nav>

        {/* Category */}
        <Link 
          to={`/insights?category=${encodeURIComponent(blog.category)}`}
          className="inline-block px-4 py-2 bg-[#fee2e2] text-[#D4AF37] rounded-full font-manrope text-sm mb-4 hover:bg-[#fecaca] transition-colors"
        >
          {blog.category}
        </Link>

        {/* Title */}
        <h1 className="font-playfair text-3xl md:text-4xl lg:text-5xl font-bold text-[#1a1a1a] mb-6 leading-tight">
          {blog.title}
        </h1>

        {/* Meta Info */}
        <div className="flex flex-wrap items-center gap-4 mb-8 pb-8 border-b border-[#e5e5e5]">
          <div className="flex items-center gap-2">
            <div className="w-10 h-10 bg-[#D4AF37] rounded-full flex items-center justify-center">
              <User className="w-5 h-5 text-white" />
            </div>
            <div>
              <p className="font-manrope text-sm font-semibold text-[#1a1a1a]">{blog.author_name}</p>
              <p className="font-manrope text-xs text-[#737373]">Author</p>
            </div>
          </div>
          
          <div className="h-8 w-px bg-[#e5e5e5]"></div>
          
          <span className="flex items-center gap-1 text-sm text-[#737373]">
            <Calendar className="w-4 h-4" />
            {formatDate(blog.published_at)}
          </span>
          
          <span className="flex items-center gap-1 text-sm text-[#737373]">
            <Clock className="w-4 h-4" />
            {blog.read_time} min read
          </span>
          
          <span className="flex items-center gap-1 text-sm text-[#737373]">
            <Eye className="w-4 h-4" />
            {blog.views} views
          </span>
        </div>

        {/* Featured Image */}
        {blog.featured_image && (
          <div className="mb-10 rounded-xl overflow-hidden">
            <img 
              src={blog.featured_image.startsWith('/') ? `${BACKEND_URL}${blog.featured_image}` : blog.featured_image}
              alt={blog.title}
              className="w-full h-auto object-cover"
            />
          </div>
        )}

        {/* Content */}
        <div 
          className="prose prose-lg max-w-none font-manrope
            prose-headings:font-playfair prose-headings:text-[#1a1a1a]
            prose-p:text-[#4a4a4a] prose-p:leading-relaxed
            prose-a:text-[#D4AF37] prose-a:no-underline hover:prose-a:underline
            prose-strong:text-[#1a1a1a]
            prose-ul:text-[#4a4a4a] prose-ol:text-[#4a4a4a]
            prose-blockquote:border-l-[#D4AF37] prose-blockquote:bg-[#fafafa] prose-blockquote:py-4 prose-blockquote:px-6 prose-blockquote:rounded-r-lg
            prose-img:rounded-lg"
          dangerouslySetInnerHTML={{ __html: blog.content }}
        />

        {/* Tags */}
        {blog.tags && blog.tags.length > 0 && (
          <div className="mt-10 pt-8 border-t border-[#e5e5e5]">
            <div className="flex flex-wrap items-center gap-2">
              <Tag className="w-4 h-4 text-[#737373]" />
              {blog.tags.map((tag, idx) => (
                <Link
                  key={idx}
                  to={`/insights?tag=${encodeURIComponent(tag)}`}
                  className="px-3 py-1 bg-[#f5f5f5] text-[#737373] rounded-full font-manrope text-sm hover:bg-[#e5e5e5] transition-colors"
                >
                  {tag}
                </Link>
              ))}
            </div>
          </div>
        )}

        {/* Share Buttons */}
        <div className="mt-8 p-6 bg-[#fafafa] rounded-xl">
          <div className="flex flex-wrap items-center justify-between gap-4">
            <div className="flex items-center gap-2">
              <Share2 className="w-5 h-5 text-[#737373]" />
              <span className="font-manrope text-sm text-[#737373]">Share this article</span>
            </div>
            <div className="flex items-center gap-2">
              <button
                onClick={() => handleShare('twitter')}
                className="p-3 bg-[#1DA1F2] text-white rounded-lg hover:opacity-90 transition-opacity"
                title="Share on Twitter"
              >
                <Twitter className="w-5 h-5" />
              </button>
              <button
                onClick={() => handleShare('linkedin')}
                className="p-3 bg-[#0A66C2] text-white rounded-lg hover:opacity-90 transition-opacity"
                title="Share on LinkedIn"
              >
                <Linkedin className="w-5 h-5" />
              </button>
              <button
                onClick={() => handleShare('facebook')}
                className="p-3 bg-[#1877F2] text-white rounded-lg hover:opacity-90 transition-opacity"
                title="Share on Facebook"
              >
                <Facebook className="w-5 h-5" />
              </button>
              <button
                onClick={() => handleShare('copy')}
                className="p-3 bg-[#1a1a1a] text-white rounded-lg hover:opacity-90 transition-opacity"
                title="Copy link"
              >
                <LinkIcon className="w-5 h-5" />
              </button>
            </div>
          </div>
        </div>

        {/* Back to Insights */}
        <div className="mt-8">
          <Link
            to="/insights"
            className="inline-flex items-center gap-2 text-[#D4AF37] font-manrope hover:underline"
          >
            <ArrowLeft className="w-4 h-4" />
            Back to all insights
          </Link>
        </div>
      </article>

      {/* Related Articles */}
      {relatedBlogs.length > 0 && (
        <section className="bg-[#fafafa] py-16">
          <div className="max-w-7xl mx-auto px-6 md:px-12">
            <h2 className="font-playfair text-2xl md:text-3xl font-bold text-[#1a1a1a] mb-8">
              Related Articles
            </h2>
            
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              {relatedBlogs.map(related => (
                <Link 
                  key={related.id}
                  to={`/insights/${related.slug}`}
                  className="group bg-white rounded-xl overflow-hidden shadow-sm hover:shadow-lg transition-shadow"
                >
                  <div className="h-40 bg-[#f5f5f5] overflow-hidden">
                    {related.featured_image ? (
                      <img 
                        src={related.featured_image.startsWith('/') ? `${BACKEND_URL}${related.featured_image}` : related.featured_image}
                        alt={related.title}
                        className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
                      />
                    ) : (
                      <div className="w-full h-full flex items-center justify-center bg-gradient-to-br from-[#D4AF37] to-[#B8960F]">
                        <BookOpen className="w-10 h-10 text-white/50" />
                      </div>
                    )}
                  </div>
                  <div className="p-4">
                    <span className="text-xs text-[#D4AF37] font-manrope uppercase tracking-wide">
                      {related.category}
                    </span>
                    <h3 className="font-playfair text-lg font-bold text-[#1a1a1a] mt-2 line-clamp-2 group-hover:text-[#D4AF37] transition-colors">
                      {related.title}
                    </h3>
                    <div className="flex items-center gap-3 mt-3 text-xs text-[#737373]">
                      <span>{related.read_time} min read</span>
                      <span>•</span>
                      <span>{related.views} views</span>
                    </div>
                  </div>
                </Link>
              ))}
            </div>
          </div>
        </section>
      )}
      
      <Footer />
    </div>
  );
};

export default InsightDetail;
